/**
 * Iter282 · Copiloto de IA comercial — chat flotante para preguntar sobre
 * licitaciones/KPIs/mercado con datos EN VIVO (misma fuente que Licitaciones).
 * Montado solo en las páginas de la parte comercial (ver Layout.jsx).
 */
import React, { useState, useRef, useEffect } from "react";
import { API } from "@/context/AuthContext";
import { Sparkles, X, Send, Loader2, Bot, User2, Minimize2, RotateCcw, CheckCircle2, Maximize2, Minimize, ShieldAlert, Check, Ban, ExternalLink } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const SESSION_KEY = "valoriza_commercial_copilot_session";
const RECENT_Q_KEY = "valoriza_commercial_copilot_recent_questions";
const MAX_RECENT = 5;

const ACCION_LABELS = {
  crear_oportunidad: "Crear oportunidad", actualizar_oportunidad: "Actualizar oportunidad",
  crear_oferta: "Crear oferta", actualizar_oferta: "Actualizar oferta",
  asignar_responsable_oportunidad: "Asignar responsable (oportunidad)", asignar_responsable_oferta: "Asignar responsable (oferta)",
  vincular_oferta_licitacion: "Vincular oferta ↔ licitación", incorporar_adjudicacion: "Incorporar adjudicación",
  actualizar_estado_traspaso: "Actualizar estado de traspaso",
};
const CAMPO_LABELS = {
  titulo: "Título", municipio: "Municipio", tipo_servicio: "Tipo de servicio", importe_licitacion: "Importe",
  estado_crm: "Estado", responsable_comercial: "Responsable comercial", probabilidad_exito: "Probabilidad de éxito (%)",
  proxima_accion: "Próxima acción", title: "Título", licitacion: "Licitación vinculada", contract_type: "Tipo de contrato",
  municipality: "Municipio", deadline: "Fecha límite", responsible_business: "Responsable comercial",
  responsible_business_email: "Email responsable", responsible_tech: "Responsable técnico", description: "Descripción",
  status: "Estado",
};

/** Entrega E · tarjeta de confirmación de una propuesta de escritura del
 * copiloto — SIEMPRE antes/después + motivo/fuente, nunca se ejecuta sola. */
const WriteProposalCard = ({ proposal }) => {
  const [state, setState] = useState(proposal); // estado local tras confirmar/rechazar
  const [busy, setBusy] = useState(null); // "confirmar" | "rechazar" | null

  const act = async (accion) => {
    setBusy(accion);
    try {
      const token = localStorage.getItem("valoriza_token") || "";
      const resp = await fetch(`${API}/commercial-copilot/write-actions/${state.id}/${accion}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: accion === "rechazar" ? JSON.stringify({ motivo: "Rechazada desde el chat" }) : undefined,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "No se pudo procesar la propuesta");
      setState(data);
    } catch (e) {
      setState((prev) => ({ ...prev, estado: "error", error: e.message }));
    } finally {
      setBusy(null);
    }
  };

  const camposCambiados = Object.keys(state.cambios_propuestos || {});
  const antes = state.entidad_antes || {};
  const esCreacion = state.es_creacion;

  return (
    <div className="max-w-[90%] px-3 py-3 rounded-2xl text-xs bg-amber-50 border border-amber-200 shadow-sm space-y-2" data-testid="copilot-write-proposal-card">
      <div className="flex items-center gap-1.5 text-amber-800 font-semibold">
        <ShieldAlert size={13} />
        <span>{ACCION_LABELS[state.tipo_accion] || state.tipo_accion} — propuesta pendiente de confirmar</span>
      </div>
      <div className="space-y-1">
        {camposCambiados.map((campo) => (
          <div key={campo} className="grid grid-cols-[90px_1fr] gap-1.5" data-testid={`copilot-proposal-field-${campo}`}>
            <span className="text-amber-700 font-medium">{CAMPO_LABELS[campo] || campo}:</span>
            <span className="text-gray-700">
              {!esCreacion && antes[campo] !== undefined && String(antes[campo]) !== String(state.cambios_propuestos[campo]) && (
                <span className="line-through text-gray-400 mr-1">{String(antes[campo] ?? "—")}</span>
              )}
              <span className="font-semibold text-gray-900">{String(state.cambios_propuestos[campo] ?? "—")}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="text-amber-700 italic border-t border-amber-200 pt-1.5">
        <span className="font-medium not-italic">Motivo: </span>{state.motivo}
        {state.fuente ? <span> · <span className="font-medium not-italic">Fuente:</span> {state.fuente}</span> : null}
      </div>
      <div className="text-gray-500 flex items-center gap-1 flex-wrap" data-testid="copilot-proposal-alcance">
        <span className="font-medium text-gray-600">Alcance:</span> {state.alcance_descripcion || "—"}
        {state.alcance_ficha_link && (
          <a href={state.alcance_ficha_link} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 underline hover:text-gray-800" data-testid="copilot-proposal-alcance-link">
            ver ficha actual <ExternalLink size={10} />
          </a>
        )}
      </div>

      {state.estado === "propuesta" && (
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => act("confirmar")}
            disabled={!!busy}
            data-testid="copilot-proposal-confirm-btn"
            className="flex items-center gap-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5 disabled:opacity-50 transition-colors"
          >
            {busy === "confirmar" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Confirmar
          </button>
          <button
            onClick={() => act("rechazar")}
            disabled={!!busy}
            data-testid="copilot-proposal-reject-btn"
            className="flex items-center gap-1 text-xs font-semibold bg-white hover:bg-gray-50 text-gray-600 border border-gray-300 rounded-lg px-3 py-1.5 disabled:opacity-50 transition-colors"
          >
            {busy === "rechazar" ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />} Rechazar
          </button>
        </div>
      )}
      {state.estado === "ejecutada" && (
        <div className="flex items-center gap-1.5 text-emerald-700 font-semibold pt-1" data-testid="copilot-proposal-executed">
          <CheckCircle2 size={13} /> Aplicado.
          {state.ficha_link && (
            <a href={state.ficha_link} className="flex items-center gap-0.5 underline hover:text-emerald-900" data-testid="copilot-proposal-ficha-link">
              Ver ficha <ExternalLink size={11} />
            </a>
          )}
        </div>
      )}
      {state.estado === "rechazada" && (
        <div className="flex items-center gap-1.5 text-gray-500 font-semibold pt-1" data-testid="copilot-proposal-rejected">
          <Ban size={13} /> Rechazada — no se ha aplicado ningún cambio.
        </div>
      )}
      {(state.estado === "conflicto_concurrencia" || state.estado === "error") && (
        <div className="flex items-center gap-1.5 text-red-600 font-semibold pt-1" data-testid="copilot-proposal-failed">
          <ShieldAlert size={13} /> {state.error || "No se pudo aplicar."}
        </div>
      )}
    </div>
  );
};

export const CommercialCopilot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hola, soy el Copiloto Comercial. Pregúntame sobre licitaciones, hit rate, submission rate, mercado o cualquier KPI — respondo con los datos en vivo de Licitaciones/YTD Board." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState([]); // Iter316 · A01 "ejecución visible" — trail de pasos (en vez de 1 texto fugaz)
  const [fullscreen, setFullscreen] = useState(false); // Iter318 · feedback: respuestas largas se leen mejor a pantalla completa
  const [elapsed, setElapsed] = useState(0); // R01 · segundos transcurridos desde el envío (se reinicia al terminar)
  const [lastFailedText, setLastFailedText] = useState(null); // R01 · permite "Reintentar" sin volver a escribir
  // Mejora sugerida tras R01-R05 · acceso rápido a las últimas preguntas
  // reales enviadas, para no tener que reescribirlas cada vez que se quiere
  // repetir o variar levemente una consulta habitual.
  const [recentQuestions, setRecentQuestions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_Q_KEY) || "[]"); } catch { return []; }
  });
  // Preguntas frecuentes del EQUIPO (backend, >=2 personas o veces) — no solo
  // del propio navegador, pedido explícitamente por el usuario.
  const [frequentQuestions, setFrequentQuestions] = useState([]);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);
  const abortRef = useRef(null); // R01 · cancelar una consulta en curso
  const elapsedTimerRef = useRef(null);

  useEffect(() => {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    sessionIdRef.current = sid;
  }, []);

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem("valoriza_token") || "";
    fetch(`${API}/commercial-copilot/frequent-questions`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.ok ? r.json() : { questions: [] })
      .then((d) => setFrequentQuestions(d.questions || []))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open && bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const sendMessage = async (overrideText) => {
    // overrideText es string sólo cuando viene de "Reintentar"; cuando se
    // llama desde onClick de un botón, el primer argumento es el SyntheticEvent.
    const override = typeof overrideText === "string" ? overrideText : null;
    const text = (override ?? input).trim();
    if (!text || loading) return;
    setLastFailedText(null);
    if (!override) {
      setMessages((prev) => [...prev, { role: "user", content: text }]);
      setInput("");
      // Nueva pregunta real (no un reintento) -> se guarda en el historial
      // rápido, más reciente primero, sin duplicados, máx 5.
      setRecentQuestions((prev) => {
        const next = [text, ...prev.filter((q) => q !== text)].slice(0, MAX_RECENT);
        try { localStorage.setItem(RECENT_Q_KEY, JSON.stringify(next)); } catch { /* noop */ }
        return next;
      });
    }
    setLoading(true);
    setSteps([]);
    setElapsed(0);
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    // R01 · cronómetro visible mientras se espera — el usuario siempre ve
    // "lleva Xs trabajando" en vez de puntos suspensivos estáticos que no
    // distinguen "sigue vivo" de "se ha colgado".
    const startedAt = Date.now();
    elapsedTimerRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startedAt) / 1000));
    }, 1000);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem("valoriza_token") || "";
      const resp = await fetch(`${API}/commercial-copilot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: text, session_id: sessionIdRef.current }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(await resp.text());

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let gotError = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let payload;
          try { payload = JSON.parse(line.slice(6)); } catch { continue; }
          if (payload.type === "token") {
            setSteps((prev) => prev.map((s) => ({ ...s, done: true })));
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + payload.delta };
              return next;
            });
          } else if (payload.type === "status") {
            // Iter316 · A01 "ejecución visible" — cada status se añade como
            // un paso nuevo del trail; el anterior queda marcado como hecho,
            // en vez de pisarlo y perder la traza de lo que hizo el agente.
            setSteps((prev) => [...prev.map((s) => ({ ...s, done: true })), { text: payload.text || "", done: false }]);
          } else if (payload.type === "write_proposal") {
            // Entrega E · propuesta de escritura del copiloto — se añade como
            // un mensaje aparte (tarjeta), nunca se mezcla con el texto.
            setMessages((prev) => [...prev, { role: "assistant", type: "proposal", proposal: payload.proposal }, { role: "assistant", content: "" }]);
          } else if (payload.type === "error") {
            gotError = true;
            setSteps([]);
            setLastFailedText(text);
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: payload.detail || "Lo siento, ha ocurrido un error consultando los datos.", isError: true };
              return next;
            });
          }
        }
      }
      if (gotError) setSteps([]);
    } catch (e) {
      if (e.name === "AbortError") {
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", content: "Consulta cancelada por el usuario.", isError: true };
          return next;
        });
        setLastFailedText(text);
      } else {
        setLastFailedText(text);
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", content: "Lo siento, ha ocurrido un error al contactar con el copiloto. Por favor, inténtalo de nuevo.", isError: true };
          return next;
        });
      }
    } finally {
      clearInterval(elapsedTimerRef.current);
      abortRef.current = null;
      setLoading(false);
      setSteps([]);
      setElapsed(0);
    }
  };

  const cancelMessage = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="commercial-copilot-toggle"
        title="Copiloto Comercial IA"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
        style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)" }}
      >
        {open ? <X size={22} className="text-white" /> : <Sparkles size={22} className="text-white" />}
      </button>

      {open && (
        <div
          data-testid="commercial-copilot-panel"
          className={fullscreen
            ? "fixed inset-4 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
            : "fixed bottom-24 left-6 z-50 w-96 max-w-[calc(100vw-24px)] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"}
          style={fullscreen ? {} : { height: "480px" }}
        >
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ background: "linear-gradient(135deg, #0f1e4d 0%, #1e3a8a 100%)" }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles size={16} className="text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold leading-tight">Copiloto Comercial</p>
                <p className="text-blue-200 text-xs">Datos en vivo · Licitaciones / Mercado / YTD</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  const sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                  sessionStorage.setItem(SESSION_KEY, sid);
                  sessionIdRef.current = sid;
                  setMessages([
                    { role: "assistant", content: "Hola, soy el Copiloto Comercial. Pregúntame sobre licitaciones, hit rate, submission rate, mercado o cualquier KPI — respondo con los datos en vivo de Licitaciones/YTD Board." },
                  ]);
                }}
                title="Nueva conversación"
                className="text-white/70 hover:text-white transition-colors p-1 rounded"
                data-testid="commercial-copilot-new-chat"
              >
                <RotateCcw size={15} />
              </button>
              <button onClick={() => setFullscreen((v) => !v)} title={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                className="text-white/70 hover:text-white transition-colors p-1 rounded" data-testid="commercial-copilot-fullscreen-toggle">
                {fullscreen ? <Minimize size={15} /> : <Maximize2 size={15} />}
              </button>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition-colors p-1 rounded" data-testid="commercial-copilot-minimize">
                <Minimize2 size={16} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50/50" data-testid="commercial-copilot-messages">
            {messages.map((msg, i) => (
              msg.type === "proposal" ? (
                <div key={i} className="flex gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#1e3a8a]">
                    <Bot size={13} className="text-white" />
                  </div>
                  <WriteProposalCard proposal={msg.proposal} />
                </div>
              ) : (
              <div key={i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${msg.role === "assistant" ? "bg-[#1e3a8a]" : "bg-gray-300"}`}>
                  {msg.role === "assistant" ? <Bot size={13} className="text-white" /> : <User2 size={13} className="text-white" />}
                </div>
                <div
                  className={`${fullscreen ? "max-w-[70%]" : "max-w-[80%]"} px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    msg.role === "assistant"
                      ? msg.isError
                        ? "bg-red-50 border border-red-200 text-red-700 rounded-tl-none shadow-sm"
                        : "bg-white border border-gray-100 text-gray-800 rounded-tl-none shadow-sm prose prose-sm prose-a:text-blue-700 prose-a:font-semibold max-w-none"
                      : "bg-[#1e3a8a] text-white rounded-tr-none"
                  }`}
                  style={{ whiteSpace: msg.role === "assistant" ? "normal" : "pre-wrap" }}
                >
                  {msg.content ? (
                    msg.role === "assistant" && !msg.isError ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown> : msg.content
                  ) : (loading && i === messages.length - 1 ? (
                    <span className="flex gap-1 items-center h-4">
                      {[0, 1, 2].map((j) => <span key={j} className="w-1.5 h-1.5 rounded-full bg-[#1e3a8a] animate-bounce" style={{ animationDelay: `${j * 0.15}s` }} />)}
                    </span>
                  ) : "")}
                  {msg.isError && i === messages.length - 1 && lastFailedText && (
                    <button
                      onClick={() => sendMessage(lastFailedText)}
                      data-testid="commercial-copilot-retry"
                      className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-900 underline"
                    >
                      <RotateCcw size={11} /> Reintentar
                    </button>
                  )}
                </div>
              </div>
              )
            ))}
            {loading && (
              <div className="flex gap-2 items-center pl-9" data-testid="commercial-copilot-elapsed">
                <span className="text-[11px] text-gray-400">
                  {elapsed < 30
                    ? `Trabajando… ${elapsed}s`
                    : elapsed < 90
                    ? `Esto está tardando más de lo habitual (${elapsed}s) — seguimos trabajando…`
                    : `Lleva ${elapsed}s — puedes cancelar y reintentar si quieres.`}
                </span>
                <button
                  onClick={cancelMessage}
                  data-testid="commercial-copilot-cancel"
                  className="text-[11px] font-semibold text-red-600 hover:text-red-800 underline"
                >
                  Cancelar
                </button>
              </div>
            )}
            {steps.length > 0 && (
              <div className="flex gap-2" data-testid="commercial-copilot-status">
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#1e3a8a]">
                  <Loader2 size={13} className="text-white animate-spin" />
                </div>
                <div className="max-w-[80%] px-3 py-2 rounded-2xl text-xs leading-relaxed bg-blue-50 border border-blue-100 text-blue-700 rounded-tl-none space-y-1">
                  {steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`commercial-copilot-step-${i}`}>
                      {s.done ? <CheckCircle2 size={11} className="text-blue-500 flex-shrink-0" /> : <Loader2 size={11} className="animate-spin flex-shrink-0" />}
                      <span className={s.done ? "opacity-60" : "italic"}>{s.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="px-3 py-2.5 border-t border-gray-100 bg-white flex-shrink-0">
            {recentQuestions.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-2 mb-0.5" data-testid="commercial-copilot-recent-questions">
                {recentQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={loading}
                    title={q}
                    data-testid={`commercial-copilot-recent-q-${i}`}
                    className="flex-shrink-0 max-w-[180px] truncate text-[11px] bg-gray-100 hover:bg-[#1e3a8a]/10 text-gray-600 hover:text-[#1e3a8a] rounded-full px-2.5 py-1 border border-gray-200 disabled:opacity-40 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            {frequentQuestions.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-2 mb-0.5" data-testid="commercial-copilot-frequent-questions">
                <span className="flex-shrink-0 text-[10px] text-gray-400 self-center pr-0.5">Frecuentes del equipo:</span>
                {frequentQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q.text)}
                    disabled={loading}
                    title={`${q.text} (${q.count}×)`}
                    data-testid={`commercial-copilot-frequent-q-${i}`}
                    className="flex-shrink-0 max-w-[180px] truncate text-[11px] bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full px-2.5 py-1 border border-blue-200 disabled:opacity-40 transition-colors"
                  >
                    {q.text}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus-within:border-[#1e3a8a] focus-within:bg-white transition-all">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ej: ¿Cuál es nuestro hit rate este año?"
                rows={1}
                data-testid="commercial-copilot-input"
                className="flex-1 text-sm bg-transparent outline-none resize-none text-gray-800 placeholder-gray-400 max-h-24"
                style={{ overflowY: "auto" }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                data-testid="commercial-copilot-send"
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40"
                style={{ background: input.trim() && !loading ? "#1e3a8a" : "#e5e7eb" }}
              >
                {loading ? <Loader2 size={14} className="animate-spin text-gray-400" /> : <Send size={14} className={input.trim() ? "text-white" : "text-gray-400"} />}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1 px-1">Enter para enviar · Shift+Enter nueva línea</p>
          </div>
        </div>
      )}
    </>
  );
};

export default CommercialCopilot;
